import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  AppBar,
  Toolbar,
  Avatar,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Close,
  Smartphone,
  Tablet,
  Computer,
  Download,
} from '@mui/icons-material';
import EmailExporterImproved from '../utils/EmailExporterImproved';

const PreviewPanel = ({ open, onClose, blocks, settings, templateName = 'email-template' }) => {
  const [previewMode, setPreviewMode] = React.useState('desktop');
  const [useHTMLPreview, setUseHTMLPreview] = React.useState(true);

  const getPreviewWidth = () => {
    switch (previewMode) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      default:
        return '100%';
    }
  };

  // Функция для генерации HTML превью (точно такого же как при отправке)
  const generateHTMLPreview = () => {
    if (!blocks || blocks.length === 0) {
      return '<div style="text-align: center; color: #999; padding: 50px;">No content to preview</div>';
    }

    try {
      return EmailExporterImproved.generateHTML(blocks, settings);
    } catch (error) {
      console.error('Error generating HTML preview:', error);
      return null;
    }
  };

  const renderPreviewBlocks = () => {
    if (!blocks || blocks.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            color: '#999',
            fontSize: '18px',
          }}
        >
          No content to preview
        </Box>
      );
    }

    return [...blocks]
      .sort((a, b) => a.position - b.position)
      .map((block, index) => (
        <Box key={block.id || index} sx={{ mb: 1 }}>
          {renderBlockPreview(block)}
        </Box>
      ));
  };

  const renderBlockPreview = (block) => {
    const { type, data } = block;

    switch (type) {
      case 'text':
        return (
          <Box sx={{ 
            p: `${data.padding || 10}px`, 
            fontSize: `${data.fontSize || 16}px`, 
            color: data.color || '#333',
            fontWeight: data.fontWeight || 'normal',
            fontStyle: data.fontStyle || 'normal',
            textDecoration: data.textDecoration || 'none',
            textAlign: data.textAlign || 'left',
            lineHeight: data.lineHeight || 1.5,
            fontFamily: data.fontFamily || 'Arial, sans-serif',
            backgroundColor: data.backgroundColor || 'transparent',
            marginTop: `${data.marginTop || 0}px`,
            marginBottom: `${data.marginBottom || 15}px`,
          }}>
            {data.content || 'Text content'}
          </Box>
        );
        
      case 'heading':
        return (
          <Typography
            component={data.level || 'h2'}
            sx={{
              p: `${data.padding || 10}px`,
              fontSize: `${data.fontSize || 24}px`,
              color: data.color || '#333',
              fontWeight: data.fontWeight || 'bold',
              fontStyle: data.fontStyle || 'normal',
              textDecoration: data.textDecoration || 'none',
              textAlign: data.textAlign || 'left',
              fontFamily: data.fontFamily || 'Arial, sans-serif',
              backgroundColor: data.backgroundColor || 'transparent',
              marginTop: `${data.marginTop || 20}px`,
              marginBottom: `${data.marginBottom || 15}px`,
              lineHeight: 1.2,
            }}
          >
            {data.content || 'Heading'}
          </Typography>
        );
        
      case 'image':
        return data.src ? (
          <Box sx={{ 
            textAlign: data.alignment || 'center', 
            p: `${data.padding || 0}px`,
            marginTop: `${data.marginTop || 10}px`,
            marginBottom: `${data.marginBottom || 10}px`,
            backgroundColor: data.backgroundColor || 'transparent',
          }}>
            {data.link ? (
              <a href={data.link} target="_blank" rel="noopener noreferrer">
                <img
                  src={data.src}
                  alt={data.alt || 'Image'}
                  style={{
                    width: data.width || '100%',
                    height: data.height || 'auto',
                    maxWidth: '100%',
                    borderRadius: `${data.borderRadius || 0}px`,
                    opacity: data.opacity || 1,
                    boxShadow: data.boxShadow || 'none',
                  }}
                />
              </a>
            ) : (
              <img
                src={data.src}
                alt={data.alt || 'Image'}
                style={{
                  width: data.width || '100%',
                  height: data.height || 'auto',
                  maxWidth: '100%',
                  borderRadius: `${data.borderRadius || 0}px`,
                  opacity: data.opacity || 1,
                  boxShadow: data.boxShadow || 'none',
                }}
              />
            )}
          </Box>
        ) : (
          <Box
            sx={{
              height: '100px',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              m: 1,
              borderRadius: `${data.borderRadius || 0}px`,
            }}
          >
            Image placeholder
          </Box>
        );
        
      case 'button':
        return (
          <Box sx={{ 
            textAlign: data.alignment || 'center', 
            p: 2,
            marginTop: `${data.marginTop || 15}px`,
            marginBottom: `${data.marginBottom || 15}px`,
          }}>
            <Button
              sx={{
                backgroundColor: data.backgroundColor || '#007bff',
                color: data.color || '#ffffff',
                borderRadius: `${data.borderRadius || 5}px`,
                padding: data.padding || '12px 24px',
                fontSize: `${data.fontSize || 16}px`,
                fontWeight: data.fontWeight || 'bold',
                textTransform: 'none',
                width: data.fullWidth ? '100%' : 'auto',
                '&:hover': {
                  backgroundColor: data.backgroundColor || '#007bff',
                  opacity: 0.8,
                },
              }}
            >
              {data.text || 'Button'}
            </Button>
          </Box>
        );
        
      case 'divider':
        const getDividerElement = () => {
          const commonStyles = {
            width: data.width || '100%',
            margin: data.alignment === 'center' ? '0 auto' : 
                   data.alignment === 'right' ? '0 0 0 auto' : '0',
            opacity: data.opacity || 1,
          };

          switch (data.style) {
            case 'dashed':
              return (
                <Box
                  sx={{
                    ...commonStyles,
                    height: `${data.height || 1}px`,
                    borderTop: `${data.height || 1}px dashed ${data.color || '#ddd'}`,
                  }}
                />
              );
            case 'dotted':
              return (
                <Box
                  sx={{
                    ...commonStyles,
                    height: `${data.height || 1}px`,
                    borderTop: `${data.height || 1}px dotted ${data.color || '#ddd'}`,
                  }}
                />
              );
            case 'double':
              return (
                <Box
                  sx={{
                    ...commonStyles,
                    height: `${Math.max(data.height || 1, 3)}px`,
                    borderTop: `1px solid ${data.color || '#ddd'}`,
                    borderBottom: `1px solid ${data.color || '#ddd'}`,
                    backgroundColor: 'transparent',
                  }}
                />
              );
            default: // solid
              return (
                <Box
                  sx={{
                    ...commonStyles,
                    height: `${data.height || 1}px`,
                    backgroundColor: data.color || '#ddd',
                    borderRadius: `${data.borderRadius || 0}px`,
                  }}
                />
              );
          }
        };
        
        return (
          <Box sx={{ 
            py: 2,
            marginTop: `${data.marginTop || 20}px`,
            marginBottom: `${data.marginBottom || 20}px`,
            padding: `${data.padding || 0}px`,
            backgroundColor: data.backgroundColor || 'transparent',
            textAlign: data.alignment || 'center',
          }}>
            {getDividerElement()}
          </Box>
        );
        
      case 'spacer':
        return (
          <Box
            sx={{
              height: `${data.height || 30}px`,
              backgroundColor: data.backgroundColor || 'transparent',
            }}
          />
        );
        
      case 'list':
        const listStyle = data.listType === 'numbered' ? 'decimal' : 
                         data.listType === 'none' ? 'none' : 'disc';
        const paddingLeft = data.listType === 'none' ? '0' : '20px';
        
        return (
          <Box sx={{ 
            marginTop: `${data.marginTop || 15}px`,
            marginBottom: `${data.marginBottom || 15}px`,
            padding: `${data.padding || 10}px`,
            backgroundColor: data.backgroundColor || 'transparent',
            textAlign: data.alignment || 'left',
          }}>
            <List sx={{ 
              listStyle: listStyle,
              paddingLeft: paddingLeft,
              margin: 0,
              fontSize: `${data.fontSize || 16}px`,
              color: data.color || '#333',
              lineHeight: data.lineHeight || 1.6,
              fontWeight: data.fontWeight || 'normal',
              fontFamily: data.fontFamily || 'Arial',
            }}>
              {(data.items || ['Item 1', 'Item 2']).map((item, index) => (
                <ListItem key={index} sx={{ 
                  display: 'list-item', 
                  padding: 0,
                  marginBottom: `${data.spacing || 8}px`,
                }}>
                  <ListItemText primary={item} />
                </ListItem>
              ))}
            </List>
          </Box>
        );
        
      case 'hero':
        return (
          <Box
            sx={{
              backgroundColor: data.backgroundColor || '#f8f9fa',
              color: data.textColor || '#333',
              textAlign: data.textAlign || 'center',
              padding: data.padding || '60px 20px',
              backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative',
              minHeight: '200px',
              marginTop: `${data.marginTop || 0}px`,
              marginBottom: `${data.marginBottom || 0}px`,
            }}
          >
            {data.backgroundImage && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                }}
              />
            )}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {data.title && (
                <Typography variant="h3" sx={{ 
                  mb: 2, 
                  fontWeight: 'bold',
                  fontSize: `${data.titleFontSize || 32}px`,
                  color: data.titleColor || data.textColor || '#333',
                }}>
                  {data.title}
                </Typography>
              )}
              {(data.subtitle || data.description) && (
                <Typography variant="h6" sx={{ 
                  mb: 3, 
                  opacity: 0.9,
                  fontSize: `${data.descriptionFontSize || 18}px`,
                  color: data.descriptionColor || data.subtitleColor || data.textColor || '#666',
                }}>
                  {data.subtitle || data.description}
                </Typography>
              )}
              {(data.showButton !== false) && (data.buttonText || data.button?.text) && (
                <Button
                  sx={{
                    backgroundColor: data.buttonBackgroundColor || '#007bff',
                    color: data.buttonTextColor || '#ffffff',
                    padding: '12px 30px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  {data.buttonText || data.button?.text}
                </Button>
              )}
            </Box>
          </Box>
        );
        
      case 'testimonial':
        return (
          <Box
            sx={{
              padding: `${data.padding || 30}px`,
              marginTop: `${data.marginTop || 20}px`,
              marginBottom: `${data.marginBottom || 20}px`,
              backgroundColor: data.backgroundColor || '#f8f9fa',
              borderRadius: `${data.borderRadius || 8}px`,
              textAlign: data.alignment || 'center',
              fontFamily: data.fontFamily || 'Arial',
            }}
          >
            {/* Avatar */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Avatar
                src={data.authorImage}
                alt={data.author || 'Author'}
                sx={{
                  width: 60,
                  height: 60,
                  backgroundColor: '#e0e0e0',
                }}
              />
            </Box>
            
            {/* Quote */}
            <Typography
              sx={{
                fontSize: `${data.quoteFontSize || 16}px`,
                color: data.quoteColor || data.textColor || '#333',
                fontStyle: 'italic',
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              "{data.quote || 'This is an amazing product!'}"
            </Typography>
            
            {/* Author */}
            <Typography
              sx={{
                fontSize: `${data.authorFontSize || 14}px`,
                color: data.authorColor || '#666',
                fontWeight: 'bold',
              }}
            >
              — {data.author || 'John Doe'}
            </Typography>
            
            {data.position && (
              <Typography
                sx={{
                  fontSize: `${(data.authorFontSize || 14) * 0.9}px`,
                  color: data.authorColor || '#666',
                  mt: 0.5,
                }}
              >
                {data.position}
              </Typography>
            )}
            
            {data.companyName && (
              <Typography
                sx={{
                  fontSize: `${(data.authorFontSize || 14) * 0.9}px`,
                  color: data.authorColor || '#666',
                  mt: 0.5,
                }}
              >
                {data.companyName}
              </Typography>
            )}
          </Box>
        );
        
      case 'product':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: previewMode === 'mobile' ? 'column' : 'row',
              alignItems: data.alignment === 'center' ? 'center' : 'flex-start',
              textAlign: data.alignment || 'left',
              padding: data.padding || '20px',
              marginTop: `${data.marginTop || 20}px`,
              marginBottom: `${data.marginBottom || 20}px`,
              gap: '20px',
            }}
          >
            {/* Product Image */}
            {data.image ? (
              <img
                src={data.image}
                alt={data.name || 'Product'}
                style={{
                  width: previewMode === 'mobile' ? '100%' : '200px',
                  height: previewMode === 'mobile' ? '200px' : '150px',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  flexShrink: 0,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: previewMode === 'mobile' ? '100%' : '200px',
                  height: previewMode === 'mobile' ? '200px' : '150px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#999',
                  flexShrink: 0,
                }}
              >
                Product Image
              </Box>
            )}
            
            {/* Product Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: previewMode === 'mobile' ? '20px' : '24px',
                  fontWeight: 'bold',
                  color: data.nameColor || '#333',
                  marginBottom: '10px',
                }}
              >
                {data.name || 'Product Name'}
              </Typography>
              
              <Typography
                sx={{
                  fontSize: previewMode === 'mobile' ? '14px' : '16px',
                  color: data.descriptionColor || '#666',
                  marginBottom: '15px',
                  lineHeight: 1.5,
                }}
              >
                {data.description || 'Product description...'}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: previewMode === 'mobile' ? '18px' : '20px',
                    fontWeight: 'bold',
                    color: data.priceColor || '#e74c3c',
                  }}
                >
                  {data.price || '$99.99'}
                </Typography>
                {data.originalPrice && (
                  <Typography
                    sx={{
                      fontSize: previewMode === 'mobile' ? '14px' : '16px',
                      color: '#999',
                      textDecoration: 'line-through',
                    }}
                  >
                    {data.originalPrice}
                  </Typography>
                )}
              </Box>
              
              <Button
                sx={{
                  backgroundColor: data.buttonBackgroundColor || '#007bff',
                  color: '#ffffff',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                {data.buttonText || 'Buy Now'}
              </Button>
            </Box>
          </Box>
        );
        
      case 'event':
        return (
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: data.padding || '25px',
              marginTop: `${data.marginTop || 20}px`,
              marginBottom: `${data.marginBottom || 20}px`,
              backgroundColor: data.backgroundColor || '#ffffff',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                margin: '0 0 15px 0',
                color: '#333',
                fontSize: '22px',
                fontWeight: 'bold',
              }}
            >
              {data.title || 'Event Title'}
            </Typography>
            
            <Typography sx={{ marginBottom: '10px', color: '#666' }}>
              <strong>Date:</strong> {data.date || new Date().toISOString().split('T')[0]}
            </Typography>
            
            <Typography sx={{ marginBottom: '10px', color: '#666' }}>
              <strong>Time:</strong> {data.time || '19:00'}
            </Typography>
            
            <Typography sx={{ marginBottom: '15px', color: '#666' }}>
              <strong>Location:</strong> {data.location || 'Event Location'}
            </Typography>
            
            <Typography
              sx={{
                marginBottom: '20px',
                color: '#333',
                lineHeight: 1.6,
              }}
            >
              {data.description || 'Event description...'}
            </Typography>
            
            <Button
              sx={{
                backgroundColor: data.buttonBackgroundColor || '#007bff',
                color: '#ffffff',
                padding: '12px 24px',
                borderRadius: '5px',
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              {data.buttonText || 'Register Now'}
            </Button>
          </Box>
        );
        
      case 'social-icons':
        // SVG icon mapping to local files
        const getSvgIcon = (platformName) => {
          const normalizedName = platformName.toLowerCase().replace(/[^a-z]/g, '');
          
          const svgMap = {
            // Social Media Platforms
            facebook: '/images/icons/facebook.svg',
            instagram: '/images/icons/instagram.svg',
            odnoklassniki: '/images/icons/od.svg',
            linkedin: '/images/icons/linkedin.svg',
            tiktok: '/images/icons/tiktok.svg',
            twitter: '/images/icons/twitter.svg',
            twitterx: '/images/icons/twitter-x.svg',
            pinterest: '/images/icons/pinterest.svg',
            youtube: '/images/icons/youtube.svg',
            github: '/images/icons/github.svg',
            itunes: '/images/icons/itunes.svg',
            
            // Messenger Platforms
            telegram: '/images/icons/telegram.svg',
            vkontakte: '/images/icons/vkontakte.svg',
            messengerfacebook: '/images/icons/messenger-facebook.svg',
            messengerline: '/images/icons/messenger-line.svg',
            whatsapp: '/images/icons/whatsapp.svg',
            viber: '/images/icons/viber.svg',
            discord: '/images/icons/discord.svg',
          };

          return svgMap[normalizedName] || '/images/icons/facebook.svg'; // fallback
        };
        
        const getIconColor = (platformName) => {
          // Если выбран monochrome стиль, возвращаем выбранный цвет фона
          if (data.style === 'monochrome') {
            return data.backgroundColor || '#666666';
          }
          
          const normalizedName = platformName.toLowerCase().replace(/[^a-z]/g, '');
          
          const colorMap = {
            // Social Media Platforms
            facebook: '#1877f2',
            instagram: '#e4405f',
            odnoklassniki: '#ee8208',
            linkedin: '#0077b5',
            tiktok: '#000000',
            twitter: '#1da1f2',
            twitterx: '#000000',
            pinterest: '#bd081c',
            youtube: '#ff0000',
            github: '#181717',
            itunes: '#000000',
            
            // Messenger Platforms
            telegram: '#0088cc',
            vkontakte: '#4680c2',
            messengerfacebook: '#0084ff',
            messengerline: '#00c300',
            whatsapp: '#25d366',
            viber: '#665cac',
            discord: '#5865f2',
          };
          
          return colorMap[normalizedName] || '#666666';
        };
        
        // Filter platforms to only show allowed ones
        const SOCIAL_MEDIA_PLATFORMS = [
          'Facebook', 'Instagram', 'Odnoklassniki', 'LinkedIn', 'Tik-Tok', 'Twitter', 
          'Twitter-X', 'Pinterest', 'YouTube', 'GitHub', 'Itunes'
        ];
        
        const MESSENGER_PLATFORMS = [
          'Telegram', 'Vkontakte', 'Messenger Facebook', 'Messenger Line', 
          'Whatsapp', 'Viber', 'Discord'
        ];
        
        const allVisiblePlatforms = (data.platforms || []).filter(p => {
          if (!p.show) return false;
          
          const allowedPlatforms = [
            ...SOCIAL_MEDIA_PLATFORMS,
            ...MESSENGER_PLATFORMS
          ];
          
          return allowedPlatforms.includes(p.name);
        });
        
        return (
          <Box
            sx={{
              textAlign: data.alignment || 'center',
              padding: data.padding || '15px',
              marginTop: `${data.marginTop || 20}px`,
              marginBottom: `${data.marginBottom || 20}px`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: data.alignment === 'center' ? 'center' : 
                              data.alignment === 'right' ? 'flex-end' : 'flex-start',
                alignItems: 'center',
                gap: `${data.spacing || 10}px`,
                flexWrap: 'wrap',
              }}
            >
              {allVisiblePlatforms.map((platform, index) => {
                const svgPath = getSvgIcon(platform.name);
                const iconSize = data.iconSize || 32;
                
                return (
                  <IconButton
                    key={`${platform.category}-${platform.name}-${index}`}
                    sx={{
                      width: iconSize,
                      height: iconSize,
                      padding: 0,
                      borderRadius: `${data.borderRadius || 50}%`,
                      backgroundColor: getIconColor(platform.name),
                      '&:hover': {
                        transform: 'scale(1.1)',
                        opacity: 0.8,
                      },
                    }}
                  >
                    {data.style === 'monochrome' ? (
                      <Box
                        sx={{
                          width: '60%',
                          height: '60%',
                          backgroundColor: data.iconColor || '#ffffff',
                          mask: `url(${svgPath}) no-repeat center/contain`,
                          WebkitMask: `url(${svgPath}) no-repeat center/contain`,
                        }}
                      />
                    ) : (
                      <img
                        src={svgPath}
                        alt={platform.name}
                        style={{
                          width: '60%',
                          height: '60%',
                          objectFit: 'contain',
                        }}
                      />
                    )}
                  </IconButton>
                );
              })}
            </Box>
          </Box>
        );
        
      case 'footer':
        return (
          <Box
            sx={{
              backgroundColor: data.backgroundColor || '#f8f9fa',
              color: data.textColor || '#666666',
              padding: data.padding || '30px 20px',
              textAlign: data.alignment || 'center',
              fontSize: `${data.fontSize || 12}px`,
              lineHeight: 1.5,
              marginTop: `${data.marginTop || 0}px`,
              marginBottom: `${data.marginBottom || 0}px`,
            }}
          >
            {data.companyName && (
              <Typography sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                {data.companyName}
              </Typography>
            )}
            
            {data.address && (
              <Typography sx={{ marginBottom: '15px' }}>
                {data.address}
              </Typography>
            )}
            
            {/* Contact Information */}
            <Box sx={{ marginBottom: '15px' }}>
              {data.phone && (
                <Typography sx={{ marginBottom: '5px' }}>
                  📞 {data.phone}
                </Typography>
              )}
              {data.email && (
                <Typography sx={{ marginBottom: '5px' }}>
                  ✉️ {data.email}
                </Typography>
              )}
              {data.website && (
                <Typography sx={{ marginBottom: '5px' }}>
                  🌐 {data.website}
                </Typography>
              )}
            </Box>
            
            {data.unsubscribeText && (
              <Typography sx={{ color: data.textColor || '#666666' }}>
                {data.unsubscribeText}
              </Typography>
            )}
          </Box>
        );
        
      case 'columns':
      case 'columns-3':
        // Simplified preview for columns
        const columnCount = data.columns || (type === 'columns-3' ? 3 : 2);

        return (
          <Box
            sx={{
              display: 'flex',
              gap: `${data.columnGap || 20}px`,
              marginTop: `${data.marginTop || 15}px`,
              marginBottom: `${data.marginBottom || 15}px`,
              padding: `${data.padding || 0}px`,
              flexDirection: previewMode === 'mobile' ? 'column' : 'row',
            }}
          >
            {Array.from({ length: columnCount }).map((_, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  border: '1px dashed #ddd',
                  padding: '10px',
                  backgroundColor: '#f9f9f9',
                  borderRadius: '4px',
                  textAlign: 'center',
                  color: '#999',
                }}
              >
                Column {index + 1}
                <br />
                <small>Content goes here</small>
              </Box>
            ))}
          </Box>
        );
        
      case 'container':
        return (
          <Box
            sx={{
              backgroundColor: data.backgroundColor || '#ffffff',
              padding: data.padding || '20px',
              borderRadius: `${data.borderRadius || 0}px`,
              border: data.border || 'none',
              maxWidth: data.maxWidth || '100%',
              marginTop: `${data.marginTop || 15}px`,
              marginBottom: `${data.marginBottom || 15}px`,
              opacity: data.opacity || 1,
              boxShadow: data.boxShadow || 'none',
              backgroundImage: data.backgroundImage ? `url(${data.backgroundImage})` : 'none',
              backgroundSize: data.backgroundSize || 'cover',
              backgroundPosition: data.backgroundPosition || 'center',
            }}
          >
            <Box
              sx={{
                border: '1px dashed #ddd',
                padding: '20px',
                textAlign: 'center',
                color: '#999',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                borderRadius: '4px',
              }}
            >
              Container Content
              <br />
              <small>Nested elements would appear here</small>
            </Box>
          </Box>
        );
        
      case 'contactUs':
        const contacts = data.contacts || [];
        const cardsPerRow = data.cardsPerRow || 2;
        
        return (
          <Box
            sx={{
              padding: `${data.padding || 30}px`,
              marginTop: `${data.marginTop || 20}px`,
              marginBottom: `${data.marginBottom || 20}px`,
              backgroundColor: data.backgroundColor || '#ffffff',
              textAlign: data.alignment || 'center',
            }}
          >
            {data.title && (
              <Typography
                sx={{
                  fontSize: `${data.titleFontSize || 28}px`,
                  color: data.titleColor || '#333333',
                  fontWeight: 'bold',
                  marginBottom: '30px',
                }}
              >
                {data.title}
              </Typography>
            )}
            
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(cardsPerRow, contacts.length)}, 1fr)`,
                gap: `${data.cardSpacing || 20}px`,
              }}
            >
              {contacts.map((contact, index) => (
                <Box
                  key={contact.id || index}
                  sx={{
                    border: `1px solid ${data.borderColor || '#e0e0e0'}`,
                    borderRadius: `${data.borderRadius || 8}px`,
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <Avatar
                    src={contact.avatar}
                    alt={contact.fullName}
                    sx={{
                      width: 80,
                      height: 80,
                      margin: '0 auto 15px auto',
                      backgroundColor: '#e0e0e0',
                    }}
                  />
                  
                  <Typography
                    sx={{
                      fontSize: `${data.nameFontSize || 18}px`,
                      color: data.nameColor || '#333333',
                      fontWeight: 'bold',
                      marginBottom: '5px',
                    }}
                  >
                    {contact.fullName}
                  </Typography>
                  
                  <Typography
                    sx={{
                      fontSize: `${data.positionFontSize || 14}px`,
                      color: data.positionColor || '#666666',
                      marginBottom: '10px',
                    }}
                  >
                    {contact.position}
                  </Typography>
                  
                  <Typography
                    sx={{
                      fontSize: `${data.descriptionFontSize || 14}px`,
                      color: data.descriptionColor || '#666666',
                      marginBottom: '15px',
                      lineHeight: 1.5,
                    }}
                  >
                    {contact.description}
                  </Typography>
                  
                  <Box>
                    {contact.phone && (
                      <Typography
                        sx={{
                          fontSize: `${data.contactFontSize || 14}px`,
                          color: data.contactColor || '#007bff',
                          marginBottom: '5px',
                        }}
                      >
                        📞 {contact.phone}
                      </Typography>
                    )}
                    {contact.email && (
                      <Typography
                        sx={{
                          fontSize: `${data.contactFontSize || 14}px`,
                          color: data.contactColor || '#007bff',
                        }}
                      >
                        ✉️ {contact.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        );
        
      case 'unsubscribe':
        return (
          <Box
            sx={{
              backgroundColor: data.backgroundColor || '#f8f9fa',
              borderRadius: `${data.borderRadius || 4}px`,
              padding: `${data.padding || 20}px`,
              marginTop: `${data.marginTop || 30}px`,
              marginBottom: `${data.marginBottom || 20}px`,
              textAlign: data.textAlign || 'center',
              border: '2px solid #ffc107',
            }}
          >
            <Typography
              sx={{
                color: data.textColor || '#666666',
                fontSize: `${data.fontSize || 12}px`,
                lineHeight: 1.5,
                marginBottom: '15px',
              }}
            >
              {data.text || "You are receiving this email because you subscribed to our newsletter."}
            </Typography>
            
            <Button
              sx={{
                backgroundColor: data.buttonColor || '#dc3545',
                color: data.buttonTextColor || '#ffffff',
                padding: '10px 20px',
                borderRadius: '4px',
                textDecoration: 'none',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {data.buttonText || 'Unsubscribe'}
            </Button>
          </Box>
        );
        
      default:
        return (
          <Box
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              border: '1px dashed #ccc',
              textAlign: 'center',
              color: '#999',
            }}
          >
            {type} block - Preview not available
          </Box>
        );
    }
  };

  const handleExport = () => {
    const html = EmailExporterImproved.generateHTML(blocks, settings);
    EmailExporterImproved.downloadHTML(html, templateName);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      <AppBar position="static" sx={{ backgroundColor: '#2c3e50' }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Template Preview
          </Typography>
          
          {/* Кнопки режимов просмотра */}
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <IconButton
              color={previewMode === 'desktop' ? 'primary' : 'inherit'}
              onClick={() => setPreviewMode('desktop')}
              title="Desktop View"
            >
              <Computer />
            </IconButton>
            <IconButton
              color={previewMode === 'tablet' ? 'primary' : 'inherit'}
              onClick={() => setPreviewMode('tablet')}
              title="Tablet View"
            >
              <Tablet />
            </IconButton>
            <IconButton
              color={previewMode === 'mobile' ? 'primary' : 'inherit'}
              onClick={() => setPreviewMode('mobile')}
              title="Mobile View"
            >
              <Smartphone />
            </IconButton>
          </Box>

          <IconButton color="inherit" onClick={handleExport} title="Export HTML">
            <Download />
          </IconButton>
          
          <IconButton color="inherit" onClick={onClose}>
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          backgroundColor: '#f5f5f5',
          p: 3,
        }}
      >
        {useHTMLPreview ? (
          // HTML Preview - точно такой же как при отправке
          (() => {
            const htmlContent = generateHTMLPreview();
            if (htmlContent === null) {
              // Если HTML preview не работает, показываем React preview
              return (
                <Box
                  sx={{
                    width: getPreviewWidth(),
                    maxWidth: '100%',
                    backgroundColor: settings.backgroundColor || '#ffffff',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: settings.fontFamily || 'Arial, sans-serif',
                    fontSize: settings.fontSize || '14px',
                    transition: 'width 0.3s ease',
                  }}
                >
                  <Box sx={{ p: `${settings.padding || 20}px` }}>
                    <Box sx={{ mb: 2, p: 1, backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px' }}>
                      <Typography variant="caption" color="warning.main">
                        ⚠️ HTML Preview unavailable, showing React Preview instead
                      </Typography>
                    </Box>
                    {renderPreviewBlocks()}
                  </Box>
                </Box>
              );
            }
            
            return (
              <Box
                sx={{
                  width: getPreviewWidth(),
                  maxWidth: '100%',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  transition: 'width 0.3s ease',
                }}
              >
                <div 
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                  style={{ 
                    width: '100%', 
                    minHeight: '400px',
                    fontFamily: 'Arial, Helvetica, sans-serif'
                  }}
                />
              </Box>
            );
          })()
        ) : (
          // Старый React Preview (для сравнения)
          <Box
            sx={{
              width: getPreviewWidth(),
              maxWidth: '100%',
              backgroundColor: settings.backgroundColor || '#ffffff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: '8px',
              overflow: 'hidden',
              fontFamily: settings.fontFamily || 'Arial, sans-serif',
              fontSize: settings.fontSize || '14px',
              transition: 'width 0.3s ease',
            }}
          >
            <Box sx={{ p: `${settings.padding || 20}px` }}>
              {renderPreviewBlocks()}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          Preview Mode: {previewMode.charAt(0).toUpperCase() + previewMode.slice(1)}
        </Typography>
        <Button 
          onClick={() => setUseHTMLPreview(!useHTMLPreview)} 
          color="secondary"
          sx={{ mr: 1 }}
        >
          {useHTMLPreview ? 'React Preview' : 'HTML Preview'}
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewPanel; 

